import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

// Step 1 → username check
// Step 2a → existing user: enter password → login
// Step 2b → new user: enter invite code
// Step 3  → new user: set own password → register → success

export default function LoginPage() {
    const [step, setStep] = useState('username'); // 'username' | 'login' | 'invite' | 'setpw' | 'done'
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [newPw, setNewPw] = useState('');
    const [newPw2, setNewPw2] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/privat';

    const inputRef = useRef(null);
    useEffect(() => { inputRef.current?.focus(); }, [step]);

    const inputStyle = {
        background: 'var(--bg-secondary)',
        border: '2px solid var(--border)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        fontFamily: 'inherit',
        fontSize: '15px',
        padding: '13px 16px',
        width: '100%',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s',
    };

    const reset = () => {
        setStep('username');
        setPassword('');
        setInviteCode('');
        setNewPw('');
        setNewPw2('');
        setError('');
        setShowPw(false);
    };

    // Step 1 → check if username exists
    const checkUsername = async (e) => {
        e.preventDefault();
        if (!username.trim()) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/check-username?username=${encodeURIComponent(username.trim())}`);
            const data = await res.json();
            setStep(data.exists ? 'login' : 'invite');
        } catch {
            setError('Verbindungsfehler.');
        }
        setLoading(false);
    };

    // Step 2a → login existing user
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(username.trim(), password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    // Step 2b → validate invite code
    const checkInvite = async (e) => {
        e.preventDefault();
        if (!inviteCode.trim()) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/validate-invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ invite_code: inviteCode.trim() }),
            });
            const data = await res.json();
            if (!data.valid) { setError('Ungültiger Einladungscode.'); }
            else { setStep('setpw'); }
        } catch {
            setError('Verbindungsfehler.');
        }
        setLoading(false);
    };

    // Step 3 → register
    const handleRegister = async (e) => {
        e.preventDefault();
        if (newPw !== newPw2) { setError('Passwörter stimmen nicht überein.'); return; }
        if (newPw.length < 6) { setError('Mindestens 6 Zeichen.'); return; }
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ invite_code: inviteCode.trim(), username: username.trim(), password: newPw }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Fehler');
            setStep('done');
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    const slideIn = { initial: { opacity: 0, x: 18 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -18 }, transition: { duration: 0.2 } };

    const UserBadge = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <button onClick={reset} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: 0 }}>
                <ArrowLeft size={16} />
            </button>
            <div style={{
                padding: '6px 14px', borderRadius: '20px',
                background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)',
                fontSize: '14px', fontWeight: 600, color: 'var(--accent)',
            }}>
                {username}
            </div>
        </div>
    );

    const ErrorBox = () => error ? (
        <div style={{
            padding: '12px 16px', marginBottom: '16px',
            background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)',
            borderRadius: '8px', color: '#ff5050', fontSize: '14px',
        }}>{error}</div>
    ) : null;

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)', display: 'flex', alignItems: 'center' }}>
            <div className="container">
                <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                    <h1 className="section-title" style={{ fontSize: '32px', marginBottom: '8px' }}>
                        {step === 'done' ? 'Willkommen' : 'Login'}
                    </h1>
                    <p className="section-subtitle" style={{ marginBottom: '36px' }}>
                        {step === 'username' && 'Privater Bereich'}
                        {step === 'login' && 'Passwort eingeben'}
                        {step === 'invite' && 'Einladungscode eingeben'}
                        {step === 'setpw' && 'Eigenes Passwort festlegen'}
                        {step === 'done' && `Konto erstellt, ${username}!`}
                    </p>

                    <AnimatePresence mode="wait">

                        {/* Step: username */}
                        {step === 'username' && (
                            <motion.form key="username" {...slideIn} onSubmit={checkUsername}>
                                <ErrorBox />
                                <div className="form-group">
                                    <label className="form-label">Benutzername</label>
                                    <input
                                        ref={inputRef}
                                        className="form-input"
                                        type="text"
                                        name="username"
                                        id="login-username"
                                        value={username}
                                        onChange={e => { setUsername(e.target.value); setError(''); }}
                                        placeholder="Dein Benutzername"
                                        required
                                        autoComplete="username"
                                    />
                                </div>
                                {/* Verstecktes Passwortfeld im selben Formular wie der Benutzername: ohne
                                    ein Passwortfeld im DOM erkennt Chrome dieses mehrstufige Login nicht
                                    zuverlässig als Login-Formular und schlägt beim Benutzername-Feld die
                                    generische Adress-/E-Mail-Autofill statt des gespeicherten Passwort-
                                    Manager-Kontos vor. Bewusst NICHT display:none/visibility:hidden, das
                                    ignoriert Chrome für Autofill-Zwecke — stattdessen "visually hidden". */}
                                <input
                                    type="password"
                                    name="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
                                />
                                <button type="submit" className="btn-primary" disabled={loading} style={{ justifyContent: 'center' }}>
                                    {loading ? 'Prüfe…' : 'Weiter'}
                                </button>
                            </motion.form>
                        )}

                        {/* Step: login (existing user) */}
                        {step === 'login' && (
                            <motion.form key="login" {...slideIn} onSubmit={handleLogin}>
                                <UserBadge />
                                <ErrorBox />
                                <div className="form-group">
                                    <label className="form-label">Passwort</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            ref={inputRef}
                                            className="form-input"
                                            type={showPw ? 'text' : 'password'}
                                            name="password"
                                            id="login-password"
                                            value={password}
                                            onChange={e => { setPassword(e.target.value); setError(''); }}
                                            placeholder="Passwort"
                                            required
                                            style={{ paddingRight: '48px' }}
                                            autoComplete="current-password"
                                        />
                                        <button type="button" onClick={() => setShowPw(v => !v)} style={{
                                            position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: 0,
                                        }}>
                                            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <button type="submit" className="btn-primary" disabled={loading} style={{ justifyContent: 'center' }}>
                                    {loading ? 'Anmelden…' : 'Anmelden'}
                                </button>
                            </motion.form>
                        )}

                        {/* Step: invite code (new user) */}
                        {step === 'invite' && (
                            <motion.form key="invite" {...slideIn} onSubmit={checkInvite}>
                                <UserBadge />
                                <div style={{
                                    padding: '12px 16px', marginBottom: '20px',
                                    background: 'rgba(251,146,60,0.07)', border: '1px solid rgba(251,146,60,0.25)',
                                    borderRadius: '10px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5,
                                }}>
                                    Dieser Benutzername ist noch frei.<br />
                                    Gib den <strong style={{ color: 'var(--accent)' }}>Einladungscode</strong> ein um ein Konto zu erstellen.
                                </div>
                                <ErrorBox />
                                <div className="form-group">
                                    <label className="form-label">Einladungscode</label>
                                    <input
                                        ref={inputRef}
                                        style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: '17px', textAlign: 'center' }}
                                        placeholder="XXXX-XXXX"
                                        value={inviteCode}
                                        onChange={e => { setInviteCode(e.target.value.toUpperCase()); setError(''); }}
                                        maxLength={9}
                                        required
                                    />
                                </div>
                                <button type="submit" className="btn-primary" disabled={loading} style={{ justifyContent: 'center' }}>
                                    {loading ? 'Prüfe Code…' : 'Code bestätigen'}
                                </button>
                            </motion.form>
                        )}

                        {/* Step: set password (new user, valid code) */}
                        {step === 'setpw' && (
                            <motion.form key="setpw" {...slideIn} onSubmit={handleRegister}>
                                <UserBadge />
                                <ErrorBox />
                                <div className="form-group">
                                    <label className="form-label">Neues Passwort</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            ref={inputRef}
                                            type={showPw ? 'text' : 'password'}
                                            style={{ ...inputStyle, paddingRight: '48px' }}
                                            placeholder="Mindestens 6 Zeichen"
                                            value={newPw}
                                            onChange={e => { setNewPw(e.target.value); setError(''); }}
                                            required
                                            autoComplete="new-password"
                                        />
                                        <button type="button" onClick={() => setShowPw(v => !v)} style={{
                                            position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: 0,
                                        }}>
                                            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Passwort wiederholen</label>
                                    <input
                                        type={showPw ? 'text' : 'password'}
                                        style={{
                                            ...inputStyle,
                                            borderColor: newPw2 && newPw !== newPw2 ? '#ff5050' : 'var(--border)',
                                        }}
                                        placeholder="Passwort bestätigen"
                                        value={newPw2}
                                        onChange={e => { setNewPw2(e.target.value); setError(''); }}
                                        required
                                        autoComplete="new-password"
                                    />
                                </div>
                                <button type="submit" className="btn-primary" disabled={loading} style={{ justifyContent: 'center' }}>
                                    {loading ? 'Konto wird erstellt…' : 'Konto erstellen'}
                                </button>
                            </motion.form>
                        )}

                        {/* Step: done */}
                        {step === 'done' && (
                            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
                                style={{ textAlign: 'center', padding: '12px 0 24px' }}>
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '50%',
                                    background: 'rgba(74,222,128,0.12)', border: '1.5px solid #4ade80',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 20px',
                                }}>
                                    <CheckCircle size={26} color="#4ade80" />
                                </div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '28px', lineHeight: 1.6 }}>
                                    Dein Konto wurde erstellt.<br />Du kannst dich jetzt einloggen.
                                </p>
                                <button className="btn-primary" onClick={() => { reset(); setUsername(username); setStep('login'); }} style={{ justifyContent: 'center', maxWidth: '200px', margin: '0 auto' }}>
                                    Zum Login
                                </button>
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
